import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MapPin, Lock, Loader2, Eye, EyeOff, Mail } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function AccountGate({ placeId, onAccountCreated }) {
  const navigate = useNavigate();
  const lead = JSON.parse(sessionStorage.getItem("audit_lead") || "{}");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("signup"); // signup | login
  const [email, setEmail] = useState(lead.email || "");

  // Skip gate entirely if user is already logged in
  useEffect(() => {
    const token = localStorage.getItem("rankify-token");
    if (token) {
      // Already logged in — link audit and skip
      (async () => {
        try {
          await axios.post(`${API}/user/link-audit`, { place_id: placeId }, {
            headers: { Authorization: `Bearer ${token}` }
          });
        } catch (e) { /* ignore */ }
        onAccountCreated();
      })();
    }
  }, [placeId, onAccountCreated]);

  const handleSignup = async () => {
    if (!password || password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/signup-and-unlock`, {
        name: lead.name || "",
        email: email || lead.email || "",
        phone: lead.phone || "",
        password,
        place_id: placeId,
      });
      const token = res.data.token;
      localStorage.setItem("rankify-token", token);
      localStorage.setItem("rankify-user", JSON.stringify(res.data.user));
      sessionStorage.removeItem("audit_lead");
      if (placeId) {
        try {
          await axios.post(`${API}/user/link-audit`, { place_id: placeId }, {
            headers: { Authorization: `Bearer ${token}` }
          });
        } catch (e) { /* best-effort link */ }
      }
      toast.success("Account created! You can now view your full report.");
      onAccountCreated();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail === "email_exists") {
        setMode("login");
        toast.error("Account already exists. Please sign in.");
      } else {
        toast.error(typeof detail === "string" ? detail : "Signup failed");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async () => {
    if (!password) { toast.error("Password is required"); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/login`, {
        email: email || lead.email || "",
        password,
      });
      const token = res.data.token;
      localStorage.setItem("rankify-token", token);
      localStorage.setItem("rankify-user", JSON.stringify(res.data.user));
      sessionStorage.removeItem("audit_lead");
      if (placeId) {
        try {
          await axios.post(`${API}/user/link-audit`, { place_id: placeId }, {
            headers: { Authorization: `Bearer ${token}` }
          });
        } catch (e) { /* best-effort link */ }
      }
      toast.success("Signed in! You can now view your full report.");
      onAccountCreated();
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center" data-testid="account-gate-overlay">
      <div className="absolute inset-0 backdrop-blur-md bg-white/30" />

      <div className="relative z-10 w-full max-w-md mx-4 bg-white rounded-2xl p-8" style={{ boxShadow: "0 25px 60px rgba(0,0,0,0.2)" }} data-testid="account-gate-card">
        <div className="text-center mb-6">
          <div className="w-12 h-12 bg-[#EEF2FF] rounded-xl flex items-center justify-center mx-auto mb-3">
            <MapPin className="w-6 h-6 text-[#3B5BDB]" />
          </div>
          <h2 className="text-xl font-heading font-bold text-[#111827]">
            {mode === "signup" ? "Your audit is ready!" : "Welcome back!"}
          </h2>
          <p className="text-sm text-[#6B7280] mt-1">
            {mode === "signup"
              ? "Create your free account to view your full report and save your results."
              : "Sign in to view your report."}
          </p>
        </div>

        {/* Always show email — read-only in both modes */}
        <div className="space-y-3 mb-4">
          {mode === "signup" && lead.name && (
            <div>
              <label className="text-xs font-medium text-[#9CA3AF] mb-1 block">Full Name</label>
              <Input value={lead.name} readOnly className="h-10 bg-[#F9FAFB] border-[#E5E7EB] text-[#6B7280]" data-testid="gate-name-display" />
            </div>
          )}
          <div>
            <label className="text-xs font-medium text-[#9CA3AF] mb-1 block">Email</label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
              <Input
                value={email}
                readOnly
                className="h-10 pl-10 bg-[#F9FAFB] border-[#E5E7EB] text-[#6B7280]"
                data-testid="gate-email-display"
              />
            </div>
          </div>
        </div>

        <div className="mb-5">
          <label className="text-xs font-medium text-[#6B7280] mb-1.5 block">
            {mode === "signup" ? "Create a Password *" : "Password *"}
          </label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
            <Input
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder={mode === "signup" ? "Minimum 8 characters" : "Your password"}
              className="pl-10 pr-10 h-12 bg-white border-[#E5E7EB] text-[#111827]"
              onKeyDown={e => e.key === "Enter" && (mode === "signup" ? handleSignup() : handleLogin())}
              data-testid="gate-password-input"
            />
            <button onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-[#9CA3AF] hover:text-[#6B7280]">
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>

        <Button
          onClick={mode === "signup" ? handleSignup : handleLogin}
          disabled={loading}
          className="w-full h-12 bg-[#3B5BDB] hover:bg-[#2F4AC7] text-white font-semibold"
          data-testid="gate-submit-btn"
        >
          {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          {mode === "signup" ? "Create Account & View Report" : "Sign In & View Report"}
        </Button>

        <div className="mt-4 space-y-2">
          <p className="text-xs text-center text-[#9CA3AF]">
            {mode === "signup" ? (
              <>Already have an account? <button onClick={() => setMode("login")} className="text-[#3B5BDB] hover:underline" data-testid="gate-switch-login">Sign in</button></>
            ) : (
              <>Don't have an account? <button onClick={() => setMode("signup")} className="text-[#3B5BDB] hover:underline" data-testid="gate-switch-signup">Create one</button></>
            )}
          </p>
          {mode === "login" && (
            <div className="flex justify-center gap-4">
              <Link to="/forgot-password" className="text-xs text-[#3B5BDB] hover:underline" data-testid="gate-forgot-password">Forgot password?</Link>
              <button onClick={() => { setEmail(""); setMode("signup"); }} className="text-xs text-[#9CA3AF] hover:underline" data-testid="gate-change-email">Not you? Change email</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
